/**
 * Presentation logic for delivery ownership (f4.2).
 *
 * Everything here is pure, so the rules that shape the owner columns and the
 * two toolbar filters are testable without rendering anything.
 *
 * Nothing in this file decides who may own what: the level a role belongs at
 * is the SERVER's answer, the role labels come from f2.2's lookup, and a
 * group with no owner renders the shared empty placeholder rather than
 * anything invented.
 *
 * An owner is an Atlas ACCOUNT, identified by `user_id`. ADR-19's separate
 * roster record is superseded: there is one row per human, so nothing here
 * has to reconcile a roster name against an account.
 */

import type { BusinessUnitGroup, CurrentHolder, CurrentOwner } from '@/api/client';
import { ApiError } from '@/api/client';
import { EMPTY_CELL } from '@/ui/emptyValue';

/**
 * The lookup type whose values the role selector offers.
 *
 * The one place this screen names a lookup type, exactly as f4.1's status
 * control names `ENTITY_STATUS`. The VALUES and their labels come from the
 * server, so a delivery role added in a later phase appears with no change
 * here -- and which of them may be held on THIS node is also the server's
 * answer, carried by the roles the assign dialog is given.
 */
export const DELIVERY_ROLE_TYPE = 'DELIVERY_ROLE';

/**
 * The two roles a Business Unit Group can have an owner for.
 *
 * Named here only to ORDER and LABEL the two prototype columns. The server
 * refuses anything else with `DELIVERY_ROLE_LEVEL_MISMATCH`, so this list can
 * never grant what the role-to-level map does not -- and when f5.1 adds CM
 * ownership it adds it at its own level, on its own screen.
 */
export const GROUP_ROLE_CODES = ['DEM', 'CEM'] as const;

export type GroupRoleCode = (typeof GROUP_ROLE_CODES)[number];

/** What a cell shows when nobody owns the role. Never blank, never invented. */
export const NO_OWNER = EMPTY_CELL;

/**
 * Anything the server resolved `current_owners` for.
 *
 * Structural rather than named, so the helpers below serve a Business Unit
 * Group, a Business Unit and whatever f6.1 and f7.1 add, without this module
 * having to import a type per level.
 */
export interface OwnerBearing {
  current_owners?: CurrentOwner[];
}

/** The current holder of one role on one node, or undefined. */
export function ownerFor(
  node: OwnerBearing,
  roleCode: string,
): CurrentOwner | undefined {
  return (node.current_owners ?? []).find(
    (owner) => owner.delivery_role_code === roleCode,
  );
}

export interface OwnerOption {
  userId: string;
  displayName: string;
}

/**
 * The distinct people who actually hold this role somewhere in the list.
 *
 * **Not every assignable account.** A filter offering forty names that match
 * no row is a worse control than one offering the three that do.
 */
export function ownerOptions(
  nodes: readonly OwnerBearing[],
  roleCode: string,
): OwnerOption[] {
  const byId = new Map<string, string>();
  for (const node of nodes) {
    const owner = ownerFor(node, roleCode);
    if (owner) byId.set(owner.user_id, owner.display_name);
  }
  return [...byId.entries()]
    .map(([userId, displayName]) => ({ userId, displayName }))
    .sort((left, right) => left.displayName.localeCompare(right.displayName));
}

/**
 * Filter by owner, CLIENT-side.
 *
 * f4.1's collection is unpaginated by decision and holds six rows, so the
 * client already has every group in full. Adding `dem_id` / `cem_id` query
 * parameters would breach f4.1's explicit *"no operation accepts a
 * pagination, search, filter or sort parameter"* in order to filter a list
 * that is already here.
 */
export function applyOwnerFilters(
  groups: readonly BusinessUnitGroup[],
  filters: Partial<Record<GroupRoleCode, string>>,
): BusinessUnitGroup[] {
  return groups.filter((group) =>
    GROUP_ROLE_CODES.every((roleCode) => {
      const wanted = filters[roleCode];
      if (!wanted) return true;
      return ownerFor(group, roleCode)?.user_id === wanted;
    }),
  );
}

/** The holder a `409 OWNER_ALREADY_ASSIGNED` named, or null. */
export function currentHolderFrom(error: unknown): CurrentHolder | null {
  return error instanceof ApiError ? error.currentHolder : null;
}

/**
 * Search the candidate accounts by NAME OR E-MAIL, case- and
 * accent-insensitively.
 *
 * The same fold the server's normalized key applies, so typing `Rozanek`
 * finds `Tomasz Różanek` in the picker exactly as it would have matched him
 * on the server.
 *
 * The e-mail is in the haystack because two accounts can legitimately carry
 * the same display name -- that is precisely why the picker renders the
 * address as a second line -- and because an operator who was given somebody
 * as `test_dem@gftus.com` should not have to guess how their name is spelled.
 * Purely a convenience for finding a row: it decides nothing, and the server
 * remains the only thing that says which account is which human.
 */
export function matchesHolder(
  displayName: string,
  email: string | null | undefined,
  search: string,
): boolean {
  const needle = fold(search);
  if (!needle) return true;
  return fold(displayName).includes(needle) || fold(email ?? '').includes(needle);
}

function fold(value: string): string {
  return value
    .trim()
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}
